import React, { useEffect, useRef, useState, useCallback } from 'react';
import DailyIframe from '@daily-co/daily-js';

const VideoChatRoom = () => {
  const callFrameRef = useRef(null);
  const [countdown, setCountdown] = useState(60);
  const [hostJoined, setHostJoined] = useState(false);
  const [hasJoinedRoom, setHasJoinedRoom] = useState(false);
  const [showApology, setShowApology] = useState(false);

  // Start countdown only after visitor has joined
  useEffect(() => {
    if (!hasJoinedRoom) return;

    const timer = setInterval(() => {
      setCountdown(prev => {
        if (prev <= 1) {
          clearInterval(timer);
          if (!hostJoined) {
            setShowApology(true); // Show apology when countdown ends and host hasn't joined
          }
          return 0;
        }
        return prev - 1;
      });
    }, 1000);

    return () => clearInterval(timer);
  }, [hasJoinedRoom, hostJoined]);

  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const roomUrl = decodeURIComponent(params.get('roomUrl'));

    if (!roomUrl) {
      console.error('No room URL provided');
      return;
    }

    const initializeCall = async () => {
      try {
        const container = document.getElementById('video-element');
        if (!container) {
          console.error('Video container not found');
          return;
        }

        const frame = DailyIframe.createFrame(container, {
          iframeStyle: {
            width: '100%',
            height: '100vh',
            border: 'none',
          },
          showLeaveButton: true,
          showFullscreenButton: true,
          theme: {
            colors: {
              accent: '#33D298'
            }
          }
        });

        // Set visitor info
        frame.setUserName('Visitor');
        
        // Join as visitor
        await frame.join({
          url: roomUrl,
          userName: 'Visitor',
          userData: {
            isHost: false
          }
        });

        // Set these after joining
        frame.setShowNamesMode('always');
        frame.setShowLocalVideo(true);

        callFrameRef.current = frame;
        setHasJoinedRoom(true);

        frame.on('participant-joined', (event) => {

          if (!event.participant.local) {
            setHostJoined(true);
          }
        });

        frame.on('joining-meeting', () => {
        });

        frame.on('joined-meeting', () => {
        });

        frame.on('error', (e) => {

        });

      } catch (err) {
        console.error('Error in call initialization:', err);
      }
    };

    initializeCall();

    return () => {
      if (callFrameRef.current) {

        callFrameRef.current.destroy();
        
        // Only refresh when actually leaving the page
        if (document.visibilityState === 'hidden') {
          fetch(`${process.env.REACT_APP_BACKEND_URL}/api/refresh-status`, {
            method: 'POST'
          }).catch(console.error);
        }
      }
    };
  }, []);

  // Function to load Calendly widget
  const loadCalendlyWidget = () => {
    const head = document.querySelector('head');
    const script = document.createElement('script');
    script.src = 'https://assets.calendly.com/assets/external/widget.js';
    head.appendChild(script);
  };

  // Load Calendly script when showing apology
  useEffect(() => {
    if (showApology) {
      loadCalendlyWidget();
    }
  }, [showApology]);

  const setupRoomWebSocket = useCallback((roomName) => {
    const ws = new WebSocket('wss://video-embed-backend.onrender.com');
    
    ws.onopen = () => {
      ws.send(JSON.stringify({ type: 'get_room_status', roomName }));
    };
    
    ws.onmessage = (event) => {
      const data = JSON.parse(event.data);
      if (data.type === 'room_status' && data.roomName === roomName) {
        if (data.status === 'declined') {
          setShowApology(true);
          setCountdown(0);
        } else if (data.status === 'accepted') {
          setHostJoined(true);
        }
      }
    };

    return ws;
  }, []);

  useEffect(() => {
    if (!hasJoinedRoom) return;

    const roomName = new URL(decodeURIComponent(new URLSearchParams(window.location.search).get('roomUrl'))).pathname.split('/').pop();
    const ws = setupRoomWebSocket(roomName);
    
    return () => ws.close();
  }, [hasJoinedRoom, setupRoomWebSocket]);

  return (
    <div className="video-chat-room" style={{ width: '100%', height: '100vh' }}>
      <div 
        id="video-element" 
        className="video-container" 
        style={{ 
          width: '100%', 
          height: '100%',
          background: '#f8f9fa' 
        }}
      />
      
      {hasJoinedRoom && countdown > 0 && !hostJoined && (
        <div className="countdown-overlay">
          <div className="countdown-content">
            <div className="countdown-number">{countdown}</div>
            <div className="countdown-text">I'll be here in less than {countdown} seconds!</div>
            <div className="running-gif-container">
              <img 
                src="/images/run-running.gif" 
                alt="Running to join the meeting!" 
                className="running-gif"
              />
            </div>
          </div>
        </div>
      )}

      {showApology && !hostJoined && (
        <div className="countdown-overlay">
          <div className="apology-content">
            <div className="apology-left">
              <div className="apology-text">
                <div className="apology-main">Sorry I'm away!</div>
                <div className="apology-sub">Let's find another time -&gt;</div>
              </div>
              <div className="sorry-gif-container">
                <img 
                  src="/images/sorry-dog.gif" 
                  alt="Sorry I missed you!" 
                  className="sorry-gif"
                />
              </div>
            </div>
            <div className="apology-right">
              <div 
                className="calendly-inline-widget" 
                data-url="https://calendly.com/alexren268/30min"
                style={{ 
                  minWidth: '320px',
                  height: '700px'
                }}
              />
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default React.memo(VideoChatRoom); 